import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  styled,
  tableCellClasses,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InputWrapper from "../../citizen/AVPForms/layout/inputWrapper";
import FileUploader from "../../citizen/AVPForms/reusableComponents/FileUploader2";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { formMTDocActions } from "@egovernments/digit-ui-module-core/src/redux/actions";
import { Col, Container, Row } from "react-bootstrap";
import useModal from "@egovernments/digit-ui-libraries/src/hooks/obps/SwalModals/useModal";

const SuggestionReviewTable = ({ formData, onSelect }) => {
  const { t } = useTranslation();
  const Modal = useModal();
  console.log("formDataFromSuggestion", formData);

  const existingSuggestions = formData?.pressReleaseDetails?.suggestions;
  const defaultSuggestionRow = { date: "", objectionNumber: "", name: "", mobileNumber: "", detailAddress: "", summary: "", attachPdf: "", decision: "", remark: "" };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      pressReleaseDetails: {
        pressReleaseDate: formData?.pressReleaseDetails?.pressReleaseDate || "",
        lastObjectionDate: formData?.pressReleaseDetails?.lastObjectionDate || "",
        suggestions: existingSuggestions?.length > 0 ? existingSuggestions : [defaultSuggestionRow],
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "pressReleaseDetails.suggestions",
  });
  const { documents } = useSelector((state) => state.formMT, shallowEqual);
  const dispatch = useDispatch();
  const { addFormMTDoc } = bindActionCreators(formMTDocActions, dispatch);
  const [showError, setShowError] = useState(false);

  // Re-populate form whenever formData changes (e.g. after API reload)
  useEffect(() => {
    if (formData?.pressReleaseDetails) {
      const suggestions = formData.pressReleaseDetails.suggestions;
      reset({
        pressReleaseDetails: {
          pressReleaseDate: formData.pressReleaseDetails.pressReleaseDate || "",
          lastObjectionDate: formData.pressReleaseDetails.lastObjectionDate || "",
          suggestions: suggestions?.length > 0 ? suggestions : [defaultSuggestionRow],
        },
      });
    }

    if (formData?.documents?.length > 0) {
      const pressReleaseDoc = formData.documents.find((doc) => doc.documentType === "pressReleasePdf");
      if (pressReleaseDoc) {
        const isAlreadyInRedux = documents?.some((d) => d.documentType === "pressReleasePdf" && d.fileStoreId === pressReleaseDoc.fileStoreId);
        if (!isAlreadyInRedux) {
          addFormMTDoc({
            documentType: "pressReleasePdf",
            fileStoreId: pressReleaseDoc.fileStoreId,
            isMandatory: pressReleaseDoc.isMandatory || true,
            additionalDetails: pressReleaseDoc.additionalDetails || { title: "Press Release PDF", fileName: "press_release.pdf" },
          });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);
  const StyleTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#1f4a7c",
      color: theme.palette.common.white,
      fontSize: 14,
      fontWeight: "bold",
      padding: "8px",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 13,
      padding: "6px 8px",
    },
  }));
  const tenantId = Digit.ULBService.getCurrentTenantId();
  const updateApiCall = Digit.Hooks.obps.useObpsAPI(tenantId, "FORMLANDUSE");
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const handleAddRow = () => {
    append({
      date: "",
      objectionNumber: "",
      name: "",
      detailAddress: "",
      summary: "",
      attachPdf: "",
      decision: "",
      remark: "",
    });
  };

  const handleRemoveRow = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };
  const handleNoWorkflow = (data) => {
    const workflow = {
      action: "NOWORKFLOW",
      comments: "",
    };

    // Merge pressReleasePdf from redux docs into the documents array
    const pressReleasePdfDoc = documents?.find((doc) => doc.documentType === "pressReleasePdf");
    const existingDocs = formData?.documents || [];
    let mergedDocuments = [...existingDocs];
    if (pressReleasePdfDoc) {
      // Remove any old pressReleasePdf entry and add the new one
      mergedDocuments = mergedDocuments.filter((d) => d.documentType !== "pressReleasePdf");
      mergedDocuments.push({
        documentType: "pressReleasePdf",
        fileStoreId: pressReleasePdfDoc.fileStoreId,
        isMandatory: true,
        additionalDetails: pressReleasePdfDoc.additionalDetails || {},
      });
    }

    const appData = {
      ...formData,
      ...data,
      documents: mergedDocuments,
      workflow,
    };
    updateApiCall.mutate(
      { LandUse: appData },
      {
        onSuccess: (result) => {
          console.log("resultFromUpdate", result);
          onSelect("", result?.LandUse?.[0]);
          Modal("Application Data Updated Successfully", "Data for suggestion review section is now updated", "success");
        },
      }
    );
  };
  const onSubmit = (data) => {
    console.log("Form Data:", data);
    handleNoWorkflow(data);
    // Handle form submission here
  };

  return (
    <div style={{ padding: "16px" }}>
      <section className="groupContainer2">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="formContainer">
            <div className="formHeaderContainer">{/* <h2 className="formHeading">{t("Document Section")}</h2> */}</div>{" "}
            <Container fluid className="formInputContainer common-app-form">
              <Row>
                <Col>
                  <InputWrapper
                    label={t("Date of Press Release")}
                    control={control}
                    placeholder={t("Enter Date of Press Release")}
                    name="pressReleaseDetails.pressReleaseDate"
                    defaultValue={formData?.pressReleaseDetails?.pressReleaseDate || ""}
                    type="datePicker"
                    mandatory
                    validation={{
                      required: {
                        value: true,
                        message: "This field is required",
                      },
                    }}
                    // disabled={userType !== "citizen"}
                  />
                </Col>
                <Col>
                  <InputWrapper
                    label={t("Last Date for Objection Submission")}
                    control={control}
                    placeholder={t("Enter Last Date for Objection Submission")}
                    name="pressReleaseDetails.lastObjectionDate"
                    defaultValue={formData?.pressReleaseDetails?.lastObjectionDate || ""}
                    type="datePicker"
                    mandatory
                    validation={{
                      required: {
                        value: true,
                        message: "This field is required",
                      },
                    }}
                    // disabled={userType !== "citizen"}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="custom-upload new-custom-uploader">
                  <FileUploader
                    isMandatory
                    label={t("Press Release PDF")}
                    control={control}
                    name="pressReleasePdf"
                    // showError={showError}
                    validationMsg="(pdf file only and Max file Size 30 MB)"
                    required
                  />
                </Col>
                <Col></Col>
              </Row>
            </Container>
          </div>
          <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddRow} size="small">
              Add Row
            </Button>
          </div>

          <TableContainer component={Paper} style={{ overflowX: "auto" }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  <StyleTableCell align="center" width="80px">
                    Date
                  </StyleTableCell>
                  {/* <StyleTableCell align="center" width="140px">
                  Objection/Suggestion Number
                </StyleTableCell> */}
                  <StyleTableCell align="center" width="120px">
                    Name
                  </StyleTableCell>
                  <StyleTableCell align="center" width="120px">
                    Mobile Number
                  </StyleTableCell>
                  <StyleTableCell align="center" width="150px">
                    Detail Address
                  </StyleTableCell>
                  <StyleTableCell align="center" width="180px">
                    Objection/Suggestion Summary
                  </StyleTableCell>
                  {/* <StyleTableCell align="center" width="120px">
                    Attach PDF
                  </StyleTableCell> */}
                  {/* <StyleTableCell align="center" width="120px">
                  Decision
                </StyleTableCell> */}
                  {/* <StyleTableCell align="center" width="140px">
                  Remark If any
                </StyleTableCell> */}
                  <StyleTableCell align="center" width="80px">
                    Action
                  </StyleTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <StyledTableRow key={field.id}>
                    {/* Date */}
                    <StyleTableCell align="center">
                      <Controller
                        name={`pressReleaseDetails.suggestions.${index}.date`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="date"
                            size="small"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { fontSize: "12px" } }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                            }}
                          />
                        )}
                      />
                    </StyleTableCell>

                    {/* Objection/Suggestion Number */}
                    {/* <StyleTableCell align="center">
                    <Controller
                      name={`suggestions.${index}.objectionNumber`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          variant="outlined"
                          placeholder="Enter number"
                          inputProps={{ style: { fontSize: "12px" } }}
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                          }}
                        />
                      )}
                    />
                  </StyleTableCell> */}

                    {/* Name */}
                    <StyleTableCell align="center">
                      <Controller
                        name={`pressReleaseDetails.suggestions.${index}.name`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            variant="outlined"
                            placeholder="Name"
                            inputProps={{ style: { fontSize: "12px" } }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                            }}
                          />
                        )}
                      />
                    </StyleTableCell>
                    <StyleTableCell align="center">
                      <Controller
                        name={`pressReleaseDetails.suggestions.${index}.mobileNumber`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            variant="outlined"
                            placeholder="Mobile Number"
                            inputProps={{ style: { fontSize: "12px" } }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                            }}
                          />
                        )}
                      />
                    </StyleTableCell>
                    {/* Detail Address */}
                    <StyleTableCell align="center">
                      <Controller
                        name={`pressReleaseDetails.suggestions.${index}.detailAddress`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            variant="outlined"
                            placeholder="Address"
                            multiline
                            rows={1}
                            inputProps={{ style: { fontSize: "12px" } }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                minHeight: "32px",
                              },
                            }}
                          />
                        )}
                      />
                    </StyleTableCell>

                    {/* Objection/Suggestion Summary */}
                    <StyleTableCell align="center">
                      <Controller
                        name={`pressReleaseDetails.suggestions.${index}.summary`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            variant="outlined"
                            placeholder="Summary"
                            multiline
                            rows={1}
                            inputProps={{ style: { fontSize: "12px" } }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                minHeight: "32px",
                              },
                            }}
                          />
                        )}
                      />
                    </StyleTableCell>

                    {/* Attach PDF */}
                    {/* <StyleTableCell align="center">
                      <FileUploader
                        control={control}
                        name={`suggestions.${index}.attachPdf`}
                        label="Attach PDF"
                        validationMsg="(pdf file only and Max file Size 10 MB)"
                        isMandatory
                        required
                        tab={1}
                        maxFiles={1}
                        // showError={showError}
                      />
                    </StyleTableCell> */}

                    {/* Decision */}
                    {/* <StyleTableCell align="center">
                    <Controller
                      name={`suggestions.${index}.decision`}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          size="small"
                          variant="outlined"
                          displayEmpty
                          sx={{
                            width: "100%",
                            height: "32px",
                            fontSize: "12px",
                          }}
                        >
                          <MenuItem value="">Select Decision</MenuItem>
                          <MenuItem value="Accepted">Accepted</MenuItem>
                          <MenuItem value="Not Accepted">Not Accepted</MenuItem>
                        </Select>
                      )}
                    />
                  </StyleTableCell> */}

                    {/* Remark If any */}
                    {/* <StyleTableCell align="center">
                    <Controller
                      name={`suggestions.${index}.remark`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          variant="outlined"
                          placeholder="Remark"
                          multiline
                          rows={1}
                          inputProps={{ style: { fontSize: "12px" } }}
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-root": {
                              minHeight: "32px",
                            },
                          }}
                        />
                      )}
                    />
                  </StyleTableCell> */}

                    {/* Action - Delete Button */}
                    <StyleTableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveRow(index)}
                        disabled={fields.length === 1}
                        title={fields.length === 1 ? "Cannot delete the last row" : "Delete row"}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyleTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div style={{ marginTop: "16px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Button variant="contained" color="primary" type="submit">
              Submit
            </Button>
            <Button variant="outlined" color="primary" onClick={() => reset()}>
              Reset
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default SuggestionReviewTable;
